import React, { useEffect, useMemo, useRef, useState } from 'react';
import LocationPicker from '../../../components/LocationPicker';
import {
  getRoute,
  routingCostingForVehicleType,
  haversineKm,
  positionOrNull,
  formatKm,
  COSTING_LABELS,
  SAME_PLACE_THRESHOLD_KM,
} from '../../../lib/melqarto';
import OwnerPicker from './OwnerPicker';
import { BigButton, CloseButton, FilterChip, StatusPill, VehicleThumb, vehicleTypeImage } from '../../../components/ui/dg';
import SuggestInput, { Suggestion } from './SuggestInput';
import {
  VEHICLE_TYPE_ORDER,
  VEHICLE_TYPE_META,
  featureOptionsFor,
  PAYMENT_METHOD_OPTIONS,
  cardSpecFieldsFor,
  conditionSpecFieldsFor,
  cleanSpecs,
  Option,
  SpecField,
} from '../../../lib/vehicleCatalog';
import {
  createVehicle,
  updateVehicle,
  uploadVehicleImages,
  deleteVehicleImage,
  searchVehicleModels,
} from '../../../api/services/vehiclesService';

/**
 * Édition complète d'une annonce depuis le back-office.
 *
 * Le formulaire de la Flotte ne couvrait que dix colonnes sur une trentaine :
 * marque, modèle, année, immatriculation, tarif horaire, position, dispo. Tout
 * ce que le propriétaire saisit depuis « Publier une location » — tarif
 * journalier, caution, plages de disponibilité, équipements, moyens de
 * paiement, caractéristiques techniques, photos, adresses de prise en charge et
 * de restitution — était invisible ici, et donc incorrigible : un signalement
 * sur une annonce ne laissait le choix qu'entre la suspendre entièrement ou ne
 * rien faire.
 *
 * Les valeurs envoyées sont les CLÉS du catalogue partagé
 * (`lib/vehicleCatalog.ts`), jamais les libellés affichés : l'API filtre en
 * silence ce qu'elle ne reconnaît pas.
 */

export type OwnerOption = {
  id: string;
  name: string;
  email?: string;
};

type VehicleEditorProps = {
  /** `null` pour une création. */
  vehicle: any | null;
  owners: OwnerOption[];
  /** Où en est le chargement de `owners` : une liste vide n'est pas une réponse. */
  ownersStatus: 'loading' | 'ready' | 'error';
  ownersError: string;
  onReloadOwners: () => void;
  canReassign: boolean;
  canDelete: boolean;
  /** Personnel : peut demander de garder la vraie plaque d'immatriculation sur les photos envoyées. */
  canKeepPlate: boolean;
  token: string;
  /** Propriétaire par défaut d'une annonce créée ici. */
  currentUserId: string;
  onClose: () => void;
  onSaved: (vehicle: any) => void;
  onDeleted: (vehicleId: string) => void;
  onError: (error: any, action: string) => void;
};

type FormState = {
  serial_number: string;
  vehicle_type: string;
  brand: string;
  model: string;
  year: string;
  is_available: boolean;
  is_suspended: boolean;
  suspension_reason: string;
  hourly_rate: string;
  daily_rate: string;
  requires_deposit: boolean;
  deposit_amount: string;
  requires_insurance: boolean;
  requires_valid_permit: boolean;
  availability_days: string;
  availability_hours: string;
  availability_rules: string;
  latitude: string;
  longitude: string;
  pickup_address: string;
  return_latitude: string;
  return_longitude: string;
  return_address: string;
  features: string[];
  payment_methods: string[];
  specs: Record<string, string>;
  /** `''` = la plateforme (owner_id NULL en base). */
  owner_id: string;
};

const TABS = [
  { key: 'annonce', label: 'Annonce' },
  { key: 'tarifs', label: 'Tarifs' },
  { key: 'disponibilite', label: 'Disponibilité' },
  { key: 'emplacement', label: 'Emplacement' },
  { key: 'conditions', label: 'Conditions' },
  { key: 'specs', label: 'Caractéristiques' },
  { key: 'photos', label: 'Photos' },
  { key: 'admin', label: 'Administration' },
] as const;

type TabKey = typeof TABS[number]['key'];

/** Six photos par annonce, comme `MAX_VEHICLE_IMAGES` côté API. */
const MAX_IMAGES = 6;

const emptyForm = (ownerId: string): FormState => ({
  serial_number: '',
  vehicle_type: 'car',
  brand: '',
  model: '',
  year: '',
  is_available: true,
  is_suspended: false,
  suspension_reason: '',
  hourly_rate: '',
  daily_rate: '',
  requires_deposit: false,
  deposit_amount: '',
  requires_insurance: false,
  requires_valid_permit: false,
  availability_days: '',
  availability_hours: '',
  availability_rules: '',
  latitude: '',
  longitude: '',
  pickup_address: '',
  return_latitude: '',
  return_longitude: '',
  return_address: '',
  features: [],
  payment_methods: [],
  specs: {},
  owner_id: ownerId,
});

const text = (value: any) => (value === null || value === undefined ? '' : String(value));

/**
 * L'annonce telle qu'elle arrive de l'API, ramenée aux chaînes du formulaire.
 *
 * `specs` peut revenir en objet JSON ou, sur de vieilles lignes, en chaîne :
 * la colonne est passée en JSONB à la migration 038 et node-postgres désérialise
 * seul, mais un appelant intermédiaire qui la relaie en texte casserait la page
 * entière sur un `Object.entries(undefined)`.
 */
const formFromVehicle = (vehicle: any): FormState => {
  const rawSpecs = typeof vehicle.specs === 'string'
    ? (() => { try { return JSON.parse(vehicle.specs); } catch { return {}; } })()
    : vehicle.specs;

  const specs: Record<string, string> = {};
  Object.entries(rawSpecs || {}).forEach(([key, value]) => { specs[key] = text(value); });

  return {
    serial_number: text(vehicle.serial_number),
    vehicle_type: vehicle.vehicle_type || 'car',
    brand: text(vehicle.brand),
    model: text(vehicle.model),
    year: text(vehicle.year),
    is_available: vehicle.is_available === true,
    is_suspended: vehicle.is_suspended === true,
    suspension_reason: text(vehicle.suspension_reason),
    hourly_rate: text(vehicle.hourly_rate),
    daily_rate: text(vehicle.daily_rate),
    requires_deposit: vehicle.requires_deposit === true,
    deposit_amount: text(vehicle.deposit_amount),
    requires_insurance: vehicle.requires_insurance === true,
    requires_valid_permit: vehicle.requires_valid_permit === true,
    availability_days: text(vehicle.availability_days),
    availability_hours: text(vehicle.availability_hours),
    availability_rules: text(vehicle.availability_rules),
    latitude: text(vehicle.latitude),
    longitude: text(vehicle.longitude),
    pickup_address: text(vehicle.pickup_address),
    return_latitude: text(vehicle.return_latitude),
    return_longitude: text(vehicle.return_longitude),
    return_address: text(vehicle.return_address),
    features: Array.isArray(vehicle.features) ? vehicle.features : [],
    payment_methods: Array.isArray(vehicle.payment_methods) ? vehicle.payment_methods : [],
    specs,
    owner_id: text(vehicle.owner_id),
  };
};

// --- Briques d'interface, pour que les huit onglets se lisent d'un bloc ------

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <div>
    <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.4px] text-[#6B7280]">{label}</label>
    {children}
    {hint && <p className="mt-1.5 text-[12.5px] leading-[1.45] text-[#6B7280]">{hint}</p>}
  </div>
);

const inputClass =
  'w-full h-[52px] px-4 border border-[#E5E7EB] rounded-2xl bg-white text-[15px] text-[#1E293B] placeholder-[#9CA3AF] focus:outline-none focus:border-[#C7C4F6] focus:ring-4 focus:ring-[#4F46E5]/10 transition-all';

/** Interrupteur sur une ligne : libellé, explication, et la bascule à droite. */
const Check: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string }> = ({ checked, onChange, label, hint }) => (
  <label className="flex cursor-pointer items-center gap-4 rounded-2xl border border-[#EEF0F4] px-4 py-3.5 transition-colors hover:bg-[#FAFAFD]">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="peer sr-only" />
    <span className="min-w-0 flex-1">
      <span className="block text-[14px] font-bold text-[#1E293B]">{label}</span>
      {hint && <span className="mt-0.5 block text-[12.5px] leading-[1.45] text-[#6B7280]">{hint}</span>}
    </span>
    <span aria-hidden="true" className={`relative h-[26px] w-11 shrink-0 rounded-full transition-colors peer-focus-visible:ring-4 peer-focus-visible:ring-[#4F46E5]/20 ${checked ? 'bg-[#4F46E5]' : 'bg-[#E5E7EB]'}`}>
      <span className={`absolute top-[3px] h-5 w-5 rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.2)] transition-all ${checked ? 'left-[21px]' : 'left-[3px]'}`} />
    </span>
  </label>
);

/** Groupe de cases à cocher rendues en pastilles (équipements, paiements). */
const Chips: React.FC<{ options: Option[]; selected: string[]; onToggle: (key: string) => void }> = ({ options, selected, onToggle }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((option) => {
      const active = selected.includes(option.key);
      return (
        <button
          key={option.key}
          type="button"
          onClick={() => onToggle(option.key)}
          className={`inline-flex h-8 items-center rounded-full border px-[13px] text-[12.5px] font-bold transition-colors ${
            active
              ? 'border-[#1E293B] bg-[#1E293B] text-white'
              : 'border-[#E5E7EB] bg-white text-[#4B5563] hover:bg-gray-50'
          }`}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

const VehicleEditor: React.FC<VehicleEditorProps> = ({
  vehicle,
  owners,
  ownersStatus,
  ownersError,
  onReloadOwners,
  canReassign,
  canDelete,
  canKeepPlate,
  token,
  currentUserId,
  onClose,
  onSaved,
  onDeleted,
  onError,
}) => {
  const isEdit = Boolean(vehicle);
  const [form, setForm] = useState<FormState>(() => (vehicle ? formFromVehicle(vehicle) : emptyForm(currentUserId)));
  const [tab, setTab] = useState<TabKey>('annonce');
  const [saving, setSaving] = useState(false);
  const [pickerTarget, setPickerTarget] = useState<'pickup' | 'return' | null>(null);

  // Longueur de l'itinéraire prise en charge → restitution, par Melqarto,
  // selon le mode de déplacement du véhicule. Dit à l'admin ce que le
  // locataire aura à faire pour rendre le véhicule — et attrape une
  // restitution posée par erreur à l'autre bout du pays.
  const [returnRoute, setReturnRoute] = useState<{ status: 'idle' | 'loading' | 'ready' | 'error'; km?: number | null }>({ status: 'idle' });
  const pickupPosition = positionOrNull(form.latitude, form.longitude);
  const returnPosition = positionOrNull(form.return_latitude, form.return_longitude);
  const returnCosting = routingCostingForVehicleType(form.vehicle_type);
  const pickupKey = pickupPosition ? `${pickupPosition.lat},${pickupPosition.lng}` : '';
  const returnKey = returnPosition ? `${returnPosition.lat},${returnPosition.lng}` : '';
  useEffect(() => {
    if (!pickupPosition || !returnPosition || !returnCosting || haversineKm(pickupPosition, returnPosition) < SAME_PLACE_THRESHOLD_KM) {
      setReturnRoute({ status: 'idle' });
      return;
    }
    const controller = new AbortController();
    setReturnRoute({ status: 'loading' });
    getRoute(pickupPosition, returnPosition, returnCosting, controller.signal)
      .then((route) => setReturnRoute({ status: 'ready', km: route.distanceKm }))
      .catch(() => { if (!controller.signal.aborted) setReturnRoute({ status: 'error' }); });
    return () => controller.abort();
    // Les clés textuelles, pas les objets : ils sont recréés à chaque rendu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupKey, returnKey, returnCosting]);
  const [images, setImages] = useState<any[]>(Array.isArray(vehicle?.images) ? vehicle.images : []);
  /** Photos choisies avant que l'annonce n'existe : envoyées juste après sa création. */
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  /**
   * Décochée par défaut : l'API remplace la plaque d'immatriculation par la
   * plaque Dingo. Cochée, les photos de l'envoi gardent la vraie plaque —
   * envoi par envoi, jamais mémorisé.
   */
  const [keepPlate, setKeepPlate] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const toggleIn = (key: 'features' | 'payment_methods') => (value: string) =>
    setForm((prev) => ({
      ...prev,
      [key]: prev[key].includes(value) ? prev[key].filter((v) => v !== value) : [...prev[key], value],
    }));

  const setSpec = (key: string, value: string) =>
    setForm((prev) => ({ ...prev, specs: { ...prev.specs, [key]: value } }));

  // Le catalogue de caractéristiques dépend du type : requalifier une voiture
  // en vélo change les champs proposés. Les valeurs déjà saisies pour l'ancien
  // type ne sont pas effacées — l'API les accepte quel que soit le type, et
  // les perdre punirait une correction de type.
  const cardSpecs = useMemo(() => cardSpecFieldsFor(form.vehicle_type), [form.vehicle_type]);
  const conditionSpecs = useMemo(() => conditionSpecFieldsFor(form.vehicle_type), [form.vehicle_type]);
  const features = useMemo(() => featureOptionsFor(form.vehicle_type), [form.vehicle_type]);

  // Marque et modèle : `GET /api/vehicle-models?type=&q=` cherche q dans la
  // marque OU le modèle et rend quinze lignes. Les marques sont dédoublonnées
  // à partir de ces lignes ; les modèles sont demandés avec la marque en
  // requête — pour n'avoir que les siens — puis filtrés sur ce qui est tapé.
  const fold = (v: string) => v.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

  const suggestBrands = async (query: string): Promise<Suggestion[]> => {
    const rows = await searchVehicleModels(form.vehicle_type, query.trim());
    const needle = fold(query.trim());
    const brands: string[] = [];
    for (const row of rows) {
      if (needle && !fold(row.brand).includes(needle)) continue;
      if (!brands.some((b) => fold(b) === fold(row.brand))) brands.push(row.brand);
    }
    return brands.map((brand) => ({ value: brand, label: brand }));
  };

  const suggestModels = async (query: string): Promise<Suggestion[]> => {
    const brand = form.brand.trim();
    const needle = fold(query.trim());
    // Sans marque : on cherche le modèle tapé, et le choisir remplit la marque.
    const rows = await searchVehicleModels(form.vehicle_type, brand || query.trim());
    return rows
      .filter((row) => (brand ? fold(row.brand) === fold(brand) : true))
      .filter((row) => !needle || fold(row.model).includes(needle) || fold(`${row.brand} ${row.model}`).includes(needle))
      .map((row) => ({
        value: row.model,
        label: row.model,
        detail: brand ? undefined : row.brand,
        onPick: brand ? undefined : () => set('brand', row.brand),
      }));
  };

  const ownerLabel = (id: string) => {
    if (!id) return 'Plateforme Dingo';
    const owner = owners.find((o) => o.id === id);
    // Un propriétaire absent de la liste n'est PAS la plateforme : c'est un
    // compte que le back-office n'a pas pu charger. Les confondre ferait lire
    // « Plateforme Dingo » sur l'annonce d'un particulier.
    if (!owner) return `Compte ${id.slice(0, 8)}… (non chargé)`;
    return owner.email ? `${owner.name} — ${owner.email}` : owner.name;
  };

  const previousOwnerId = text(vehicle?.owner_id);
  const ownerChanged = isEdit && canReassign && form.owner_id !== previousOwnerId;

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !pickerTarget) onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, pickerTarget]);

  /**
   * Ce que la base refuse en NOT NULL, refusé ici avec une phrase lisible.
   *
   * Les bornes de tarif ne sont PAS recopiées : elles dépendent du type
   * (`HOURLY_RATE_CAPS` côté API) et une deuxième table divergerait. L'API
   * renvoie un message explicite, désormais remonté tel quel par le service.
   */
  const validate = (): string | null => {
    if (!form.brand.trim()) return 'La marque est obligatoire.';
    if (!form.model.trim()) return 'Le modèle est obligatoire.';
    if (!form.year.trim()) return "L'année est obligatoire.";
    if (!form.hourly_rate.trim()) return 'Le tarif horaire est obligatoire.';
    // Le motif n'est exigé que sur une suspension PRONONCÉE ICI : c'est elle qui
    // déclenche l'email au propriétaire. Le réclamer sur une annonce déjà
    // suspendue — l'ancien menu de la liste acceptait un motif vide —
    // empêcherait d'en corriger le tarif sans inventer une justification
    // rétroactive que personne ne recevra.
    const newlySuspended = form.is_suspended && vehicle?.is_suspended !== true;
    if (newlySuspended && !form.suspension_reason.trim()) {
      return 'Indiquez le motif de la suspension : le propriétaire le reçoit par email.';
    }
    return null;
  };

  const buildPayload = () => {
    const number = (value: string) => (value.trim() === '' ? null : Number.parseFloat(value));

    const payload: any = {
      serial_number: form.serial_number.trim(),
      vehicle_type: form.vehicle_type,
      brand: form.brand.trim(),
      model: form.model.trim(),
      year: Number.parseInt(form.year, 10),
      // Découle du propriétaire, jamais d'une case à part : l'API le dérive
      // elle-même, on l'envoie cohérent pour les écrans qui lisent la réponse.
      is_platform_owned: !form.owner_id,
      is_available: form.is_available,
      hourly_rate: number(form.hourly_rate),
      latitude: number(form.latitude),
      longitude: number(form.longitude),
      return_latitude: number(form.return_latitude),
      return_longitude: number(form.return_longitude),
      daily_rate: number(form.daily_rate),
      // Envoyés même vides : une chaîne vide EFFACE le champ côté API, là où son
      // absence le laisserait inchangé. C'est ce qui rend une règle de
      // disponibilité ou une adresse supprimable depuis cet écran.
      availability_days: form.availability_days.trim(),
      availability_hours: form.availability_hours.trim(),
      availability_rules: form.availability_rules.trim(),
      pickup_address: form.pickup_address.trim(),
      return_address: form.return_address.trim(),
      features: form.features,
      payment_methods: form.payment_methods,
      requires_deposit: form.requires_deposit,
      deposit_amount: form.requires_deposit ? number(form.deposit_amount) : null,
      requires_insurance: form.requires_insurance,
      requires_valid_permit: form.requires_valid_permit,
      specs: cleanSpecs(form.specs),
      is_suspended: form.is_suspended,
      suspension_reason: form.is_suspended ? form.suspension_reason.trim() : null,
    };

    // owner_id n'est envoyé que par quelqu'un qui a le droit de le changer :
    // l'API refuse la réaffectation en 403, et un champ posé « pour rien » par
    // un modérateur ferait échouer un simple changement de prix.
    if (!isEdit) {
      payload.owner_id = form.owner_id || null;
    } else if (canReassign) {
      payload.owner_id = form.owner_id || null;
    }

    return payload;
  };

  const save = async () => {
    const problem = validate();
    if (problem) {
      // Le champ fautif n'est pas forcément sous les yeux : le formulaire est
      // à onglets. Le pied de la modale, lui, est toujours visible.
      setNotice(problem);
      return;
    }

    // Pas de confirmation pour un transfert : le bandeau ambre de l'onglet
    // Administration l'annonce déjà avant l'enregistrement, et l'enregistrement
    // EST la confirmation.
    setNotice(null);
    setSaving(true);
    try {
      const payload = buildPayload();

      if (isEdit) {
        const vehicleId = vehicle.id_vehicle || vehicle.id;
        const updated = await updateVehicle(vehicleId, payload, token);
        // `RETURNING *` ne rapporte ni les photos ni la note : elles ne sont pas
        // des colonnes de "Vehicle". Sans cette fusion, l'annonce perdrait ses
        // photos à l'écran au premier enregistrement.
        onSaved({ ...vehicle, ...updated, images });
      } else {
        const created = await createVehicle(payload, token);
        const createdId = created.id_vehicle || created.id;

        // Les photos choisies avant l'enregistrement n'avaient pas d'annonce à
        // rejoindre : la route de dépôt exige un id. Elles partent maintenant.
        let createdImages: any[] = [];
        if (pendingFiles.length > 0 && createdId) {
          try {
            createdImages = await uploadVehicleImages(createdId, pendingFiles, token, { keepPlate: canKeepPlate && keepPlate });
          } catch (error) {
            // L'annonce, elle, est bien créée : on le dit plutôt que de laisser
            // croire à un échec complet et de la faire créer deux fois.
            onError(error, "l'envoi des photos de l'annonce créée");
          }
        }
        onSaved({ ...created, images: createdImages });
      }

      onClose();
    } catch (error) {
      onError(error, isEdit ? "la modification de l'annonce" : "la création de l'annonce");
    } finally {
      setSaving(false);
    }
  };

  // Suppression en deux clics sur le même bouton, sans boîte de dialogue du
  // navigateur : le premier l'arme (« Confirmer la suppression »), le second
  // agit. L'armement retombe seul après quelques secondes, pour qu'un bouton
  // laissé armé ne surprenne pas au retour sur l'onglet.
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // Messages de validation et d'échec, affichés dans le pied de la modale —
  // visibles quel que soit l'onglet ouvert — plutôt qu'en boîte de dialogue
  // du navigateur.
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    if (!confirmingDelete) return;
    const timer = window.setTimeout(() => setConfirmingDelete(false), 4000);
    return () => window.clearTimeout(timer);
  }, [confirmingDelete]);

  const remove = () => {
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    onDeleted(vehicle.id_vehicle || vehicle.id);
  };

  const pickFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const chosen = Array.from(files);
    const total = images.length + pendingFiles.length + chosen.length;
    if (total > MAX_IMAGES) {
      setNotice(`Maximum ${MAX_IMAGES} photos par annonce (${images.length + pendingFiles.length} déjà présente(s)).`);
      return;
    }

    if (!isEdit) {
      setPendingFiles((prev) => [...prev, ...chosen]);
      return;
    }

    setUploading(true);
    try {
      const uploaded = await uploadVehicleImages(vehicle.id_vehicle || vehicle.id, chosen, token, { keepPlate: canKeepPlate && keepPlate });
      setImages((prev) => [...prev, ...(Array.isArray(uploaded) ? uploaded : [])]);
    } catch (error) {
      onError(error, "l'envoi des photos");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = async (image: any) => {
    try {
      await deleteVehicleImage(vehicle.id_vehicle || vehicle.id, image.id || image.id_vehicle_image, token);
      setImages((prev) => prev.filter((i) => (i.id || i.id_vehicle_image) !== (image.id || image.id_vehicle_image)));
    } catch (error) {
      onError(error, 'la suppression de la photo');
    }
  };

  const renderSpec = (field: SpecField) => {
    const value = form.specs[field.key] ?? '';

    if (field.input.kind === 'choice') {
      return (
        <Field key={field.key} label={field.label}>
          <select value={value} onChange={(e) => setSpec(field.key, e.target.value)} className={inputClass}>
            <option value="">Non renseigné</option>
            {field.input.options.map((option) => (
              <option key={option.key} value={option.key}>{option.label}</option>
            ))}
          </select>
        </Field>
      );
    }

    if (field.input.kind === 'text') {
      return (
        <Field key={field.key} label={field.label}>
          <input
            type="text"
            maxLength={field.input.max}
            value={value}
            placeholder={field.placeholder}
            onChange={(e) => setSpec(field.key, e.target.value)}
            className={inputClass}
          />
        </Field>
      );
    }

    const { min, max } = field.input;
    return (
      <Field key={field.key} label={field.unit ? `${field.label} (${field.unit})` : field.label} hint={`Entre ${min} et ${max}`}>
        <input
          type="number"
          min={min}
          max={max}
          step={field.input.kind === 'decimal' ? '0.1' : '1'}
          value={value}
          placeholder={field.placeholder}
          onChange={(e) => setSpec(field.key, e.target.value)}
          className={inputClass}
        />
      </Field>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0F172A]/40 p-4">
      <div className="flex max-h-[92vh] w-full max-w-[880px] flex-col overflow-hidden rounded-3xl bg-white shadow-[0_24px_60px_rgba(15,23,42,0.28)]">

        {/* En-tête : la vraie photo, sinon le rendu 3D du type */}
        <div className="flex items-center gap-4 px-7 pb-4 pt-6">
          {isEdit && <VehicleThumb vehicle={{ ...vehicle, images }} type={form.vehicle_type} size={64} radius={16} dimmed={form.is_suspended || !form.is_available} />}
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2.5">
              <h3 className="truncate text-[22px] font-extrabold leading-7 text-[#1E293B]">
                {isEdit ? ([form.brand, form.model].filter(Boolean).join(' ') || 'Modifier l\'annonce') : 'Nouvelle annonce'}
              </h3>
              {isEdit && (
                form.is_suspended ? <StatusPill tone="red">Suspendue</StatusPill>
                  : form.is_available ? <StatusPill tone="green">En ligne</StatusPill>
                  : <StatusPill tone="grey">En pause</StatusPill>
              )}
            </div>
            <p className="mt-0.5 truncate text-[13.5px] text-[#6B7280]">
              {isEdit
                ? `${VEHICLE_TYPE_META[form.vehicle_type as keyof typeof VEHICLE_TYPE_META]?.singular || 'Véhicule'} · ${ownerLabel(form.owner_id).replace(' — ', ' · ')}`
                : 'Renseignez au minimum la marque, le modèle, l\'année et le tarif horaire.'}
            </p>
          </div>
          <span className="self-start"><CloseButton onClick={onClose} /></span>
        </div>

        {/* Onglets */}
        <div className="flex flex-shrink-0 gap-[7px] overflow-x-auto border-b border-[#EEF0F4] px-7 pb-4">
          {TABS.map((t) => {
            const count = t.key === 'photos' ? images.length + pendingFiles.length : 0;
            return (
              <FilterChip key={t.key} active={tab === t.key} count={count || null} onClick={() => setTab(t.key)}>{t.label}</FilterChip>
            );
          })}
        </div>

        {/* Contenu */}
        <div className="flex-1 space-y-5 overflow-y-auto px-7 py-6">

          {tab === 'annonce' && (
            <>
              <Field label="Type de véhicule" hint="Change les équipements et les caractéristiques proposés dans les autres onglets.">
                <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-5">
                  {VEHICLE_TYPE_ORDER.map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => set('vehicle_type', type)}
                      className={`flex h-24 flex-col items-center justify-center gap-1 rounded-2xl text-[12.5px] transition-colors ${
                        form.vehicle_type === type
                          ? 'border-2 border-[#4F46E5] bg-[#F5F4FE] font-bold text-[#4F46E5]'
                          : 'border border-[#EEF0F4] font-semibold text-[#4B5563] hover:bg-[#FAFAFD]'
                      }`}
                    >
                      <img src={vehicleTypeImage(type)} alt="" className="h-[52px] w-[72px] object-contain" />
                      {VEHICLE_TYPE_META[type].singular}
                    </button>
                  ))}
                </div>
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Marque">
                  <SuggestInput
                    value={form.brand}
                    onChange={(v) => set('brand', v)}
                    fetchSuggestions={suggestBrands}
                    refreshKey={form.vehicle_type}
                    className={inputClass}
                    placeholder="Peugeot"
                  />
                </Field>
                <Field label="Modèle">
                  <SuggestInput
                    value={form.model}
                    onChange={(v) => set('model', v)}
                    fetchSuggestions={suggestModels}
                    refreshKey={`${form.vehicle_type}|${form.brand}`}
                    className={inputClass}
                    placeholder="208"
                  />
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Année">
                  <input type="number" value={form.year} onChange={(e) => set('year', e.target.value)} className={inputClass} placeholder="2024" />
                </Field>
                <Field label="Immatriculation" hint="Facultative, un vélo ou une trottinette n’en a pas.">
                  <input type="text" value={form.serial_number} onChange={(e) => set('serial_number', e.target.value)} className={inputClass} placeholder="123 TU 4567" />
                </Field>
              </div>

              <Check
                checked={form.is_available}
                onChange={(v) => set('is_available', v)}
                label="Disponible à la location"
                hint="Désactivée, l’annonce reste publiée mais n’est plus réservable."
              />
            </>
          )}

          {tab === 'tarifs' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Tarif horaire (TND/h)">
                  <input type="number" step="0.01" value={form.hourly_rate} onChange={(e) => set('hourly_rate', e.target.value)} className={inputClass} placeholder="10.00" />
                </Field>
                <Field label="Tarif journalier (TND/jour)" hint="Facultatif.">
                  <input type="number" step="0.01" value={form.daily_rate} onChange={(e) => set('daily_rate', e.target.value)} className={inputClass} placeholder="80.00" />
                </Field>
              </div>

              <Check
                checked={form.requires_deposit}
                onChange={(v) => set('requires_deposit', v)}
                label="Caution demandée"
                hint="Dingo n'encaisse rien : la caution se remet en main propre au propriétaire. Le montant n'est publié que pour que le locataire arrive avec la somme."
              />

              {form.requires_deposit && (
                <Field label="Montant de la caution (TND)">
                  <input type="number" step="0.01" value={form.deposit_amount} onChange={(e) => set('deposit_amount', e.target.value)} className={inputClass} placeholder="200" />
                </Field>
              )}

              <Field label="Moyens de paiement acceptés" hint="Réglés directement entre le locataire et le propriétaire.">
                <Chips options={PAYMENT_METHOD_OPTIONS} selected={form.payment_methods} onToggle={toggleIn('payment_methods')} />
              </Field>
            </>
          )}

          {tab === 'disponibilite' && (
            <>
              <Field label="Période" hint="Libellé affiché tel quel, ex. « 12 mars - 30 juin ».">
                <input type="text" value={form.availability_days} onChange={(e) => set('availability_days', e.target.value)} className={inputClass} placeholder="12 mars - 30 juin" />
              </Field>
              <Field label="Horaires" hint="Ex. « 08:00 - 20:00 ».">
                <input type="text" value={form.availability_hours} onChange={(e) => set('availability_hours', e.target.value)} className={inputClass} placeholder="08:00 - 20:00" />
              </Field>
              <Field label="Règles particulières" hint="Texte libre lu par le locataire avant de réserver.">
                <textarea
                  rows={4}
                  value={form.availability_rules}
                  onChange={(e) => set('availability_rules', e.target.value)}
                  className={inputClass}
                  placeholder="Pas de location le dimanche. Restitution avant 19h."
                />
              </Field>
            </>
          )}

          {tab === 'emplacement' && (
            <>
              <Field label="Prise en charge">
                <button
                  type="button"
                  onClick={() => setPickerTarget('pickup')}
                  className="flex h-[52px] w-full items-center justify-between rounded-2xl border border-[#E5E7EB] px-4 text-[14px] transition-colors hover:border-[#C7C4F6]"
                >
                  <span className="font-bold text-[#4F46E5]">Choisir sur la carte</span>
                  {form.latitude && form.longitude && (
                    <span className="font-mono text-[12px] text-[#6B7280]">
                      {parseFloat(form.latitude).toFixed(4)}, {parseFloat(form.longitude).toFixed(4)}
                    </span>
                  )}
                </button>
              </Field>
              <Field label="Adresse de prise en charge" hint="Jamais servie au catalogue public : sur une location entre particuliers, c'est le domicile du propriétaire.">
                <input type="text" value={form.pickup_address} onChange={(e) => set('pickup_address', e.target.value)} className={inputClass} placeholder="12 rue de Marseille, Tunis" />
              </Field>

              <div className="-mx-7 h-2 bg-[#F1F2F8]" />

              <Field label="Restitution" hint="Laissée vide, la restitution se fait au lieu de prise en charge.">
                <button
                  type="button"
                  onClick={() => setPickerTarget('return')}
                  className="flex h-[52px] w-full items-center justify-between rounded-2xl border border-[#E5E7EB] px-4 text-[14px] transition-colors hover:border-[#C7C4F6]"
                >
                  <span className="font-bold text-[#4F46E5]">Choisir sur la carte</span>
                  {form.return_latitude && form.return_longitude && (
                    <span className="font-mono text-[12px] text-[#6B7280]">
                      {parseFloat(form.return_latitude).toFixed(4)}, {parseFloat(form.return_longitude).toFixed(4)}
                    </span>
                  )}
                </button>
              </Field>
              <Field label="Adresse de restitution">
                <input type="text" value={form.return_address} onChange={(e) => set('return_address', e.target.value)} className={inputClass} placeholder="Même adresse" />
              </Field>

              {returnRoute.status !== 'idle' && (
                <p className="text-[12.5px] leading-[1.45] text-[#6B7280]">
                  {returnRoute.status === 'loading' && 'Calcul de l\'itinéraire de restitution…'}
                  {returnRoute.status === 'error' && 'Itinéraire de restitution indisponible pour le moment.'}
                  {returnRoute.status === 'ready' && (
                    returnRoute.km != null
                      ? <>Itinéraire de restitution : <strong className="text-[#1E293B]">{formatKm(returnRoute.km)}</strong> {COSTING_LABELS[returnCosting || 'auto']}, d'après Melqarto.</>
                      : 'Itinéraire de restitution trouvé, longueur inconnue.'
                  )}
                </p>
              )}
            </>
          )}

          {tab === 'conditions' && (
            <>
              <Field label="Équipements" hint={`Proposés pour un(e) ${VEHICLE_TYPE_META[form.vehicle_type as keyof typeof VEHICLE_TYPE_META]?.singular.toLowerCase() || 'véhicule'}.`}>
                <Chips options={features} selected={form.features} onToggle={toggleIn('features')} />
              </Field>

              <Check
                checked={form.requires_insurance}
                onChange={(v) => set('requires_insurance', v)}
                label="Assurance exigée du locataire"
              />
              <Check
                checked={form.requires_valid_permit}
                onChange={(v) => set('requires_valid_permit', v)}
                label="Permis valide exigé"
                hint="La catégorie de permis, l'âge minimum et l'ancienneté se précisent dans l'onglet Caractéristiques."
              />
            </>
          )}

          {tab === 'specs' && (
            <>
              <div>
                <h4 className="mb-3 text-[11px] font-bold uppercase tracking-[0.4px] text-[#6B7280]">Mises en avant sur les cartes</h4>
                <div className="grid grid-cols-2 gap-4">
                  {cardSpecs.map(renderSpec)}
                  {form.vehicle_type === 'bike' && renderSpec({ key: 'range_km', label: 'Autonomie', input: { kind: 'int', min: 1, max: 1000 }, unit: 'km', placeholder: 'Ex : 45' })}
                </div>
              </div>

              {conditionSpecs.length > 0 && (
                <div className="pt-2">
                  <h4 className="mb-3 text-[11px] font-bold uppercase tracking-[0.4px] text-[#6B7280]">Conditions de fin de location</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {conditionSpecs.map(renderSpec)}
                  </div>
                </div>
              )}
            </>
          )}

          {tab === 'photos' && (
            <>
              <div className="grid grid-cols-3 gap-3">
                {images.map((image) => (
                  <div key={image.id || image.id_vehicle_image} className="group relative aspect-[4/3] overflow-hidden rounded-2xl border border-[#EEF0F4] bg-[#F7F7FB]">
                    <img src={image.url} alt="" className="w-full h-full object-cover" />
                    {/* Repère connu seulement pour les photos envoyées pendant cette session :
                        l'information n'est pas en base, l'API la donne à l'upload. */}
                    {typeof image.plate_masked === 'boolean' && (
                      <span
                        className={`absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium shadow ${
                          image.plate_masked ? 'bg-indigo-600/90 text-white' : 'bg-amber-100/95 text-amber-800'
                        }`}
                      >
                        {image.plate_masked ? 'plaque masquée' : 'plaque visible'}
                      </span>
                    )}
                    <button
                      onClick={() => removeImage(image)}
                      className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-white/90 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center shadow"
                      title="Supprimer cette photo"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}

                {pendingFiles.map((file, index) => (
                  <div key={`${file.name}-${index}`} className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-dashed border-[#C7C4F6] bg-[#F5F4FE] flex flex-col items-center justify-center px-2 text-center">
                    <span className="text-[10px] text-indigo-700 truncate w-full">{file.name}</span>
                    <span className="text-[10px] text-indigo-400">envoyée à la création</span>
                    <button
                      onClick={() => setPendingFiles((prev) => prev.filter((_, i) => i !== index))}
                      className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-white/90 text-red-600 flex items-center justify-center shadow"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}

                {(images.length + pendingFiles.length) < MAX_IMAGES && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="aspect-[4/3] rounded-2xl border border-dashed border-[#D1D5DB] text-[#6B7280] hover:border-[#C7C4F6] hover:text-[#4F46E5] transition-colors flex flex-col items-center justify-center gap-1 disabled:opacity-50"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m6-6H6" />
                    </svg>
                    <span className="text-[12.5px] font-bold">{uploading ? 'Envoi…' : 'Ajouter'}</span>
                  </button>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => pickFiles(e.target.files)}
              />

              {canKeepPlate && (
                <Check
                  checked={keepPlate}
                  onChange={setKeepPlate}
                  label="Garder la vraie plaque sur ces photos"
                  hint="Sinon, la plaque d'immatriculation est remplacée par la plaque Dingo avant l'enregistrement. Vaut pour les photos envoyées maintenant, pas pour celles déjà en place."
                />
              )}

              <p className="text-[12.5px] leading-[1.45] text-[#6B7280]">
                {MAX_IMAGES} photos au maximum. Une suppression est immédiate et définitive — elle ne dépend pas du bouton « Enregistrer ».
              </p>
            </>
          )}

          {tab === 'admin' && (
            <>
              {canReassign ? (
                <Field
                  label="Propriétaire de l'annonce"
                  hint="Le transfert s'inscrit au journal d'audit. Aucun des deux comptes n'est prévenu."
                >
                  <OwnerPicker
                    owners={owners}
                    status={ownersStatus}
                    error={ownersError}
                    onRetry={onReloadOwners}
                    value={form.owner_id}
                    onChange={(next) => set('owner_id', next)}
                  />
                </Field>
              ) : (
                <Field label="Propriétaire de l'annonce">
                  <div className={`${inputClass} bg-gray-50 text-gray-500`}>{ownerLabel(form.owner_id)}</div>
                  <p className="mt-1.5 text-[12.5px] text-[#6B7280]">Seul un administrateur peut transférer une annonce.</p>
                </Field>
              )}

              {ownerChanged && (
                <div className="rounded-2xl bg-[#FFF8EB] px-4 py-3 text-[13px] leading-[1.5] text-[#92400E]">
                  Transfert en attente d'enregistrement : <strong>{ownerLabel(previousOwnerId)}</strong> → <strong>{ownerLabel(form.owner_id)}</strong>.
                </div>
              )}

              {/* Plus de case « Véhicule de la plateforme » : le drapeau suit le
                  propriétaire (Plateforme Dingo = pas de propriétaire). Une case
                  à part restait cochée après un transfert vers un vrai compte,
                  et l'annonce s'affichait encore comme celle de la plateforme. */}
              <div className="-mx-7 h-2 bg-[#F1F2F8]" />

              <Check
                checked={form.is_suspended}
                onChange={(v) => setForm((prev) => ({
                  ...prev,
                  is_suspended: v,
                  // Suspendre retire aussi de la location : laisser l'annonce
                  // « disponible » et suspendue est un état que personne ne sait
                  // lire.
                  is_available: v ? false : prev.is_available,
                  suspension_reason: v ? prev.suspension_reason : '',
                }))}
                label="Annonce suspendue"
                hint="Retirée du catalogue. Le propriétaire est prévenu par email et notification."
              />

              {form.is_suspended && (
                <Field label="Motif de la suspension" hint="Envoyé au propriétaire tel quel.">
                  <textarea
                    rows={3}
                    value={form.suspension_reason}
                    onChange={(e) => set('suspension_reason', e.target.value)}
                    className={inputClass}
                    placeholder="Photos ne correspondant pas au véhicule."
                  />
                </Field>
              )}

              {isEdit && canDelete && (
                <div className="border-t border-[#EEF0F4] pt-5">
                  <button
                    onClick={remove}
                    className={`h-12 rounded-3xl px-6 text-[15px] font-bold transition-colors ${
                      confirmingDelete
                        ? 'bg-[#DC2626] text-white hover:bg-[#B91C1C]'
                        : 'bg-[#FDECEF] text-[#B91C1C] hover:bg-[#FBDDE2]'
                    }`}
                  >
                    {confirmingDelete ? 'Confirmer la suppression' : 'Supprimer l’annonce'}
                  </button>
                  <p className="mt-1.5 text-[12.5px] text-[#6B7280]">
                    Définitif. Emporte aussi les locations en cours du véhicule.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Pied */}
        {notice && (
          <div className="flex-shrink-0 px-7 pt-3">
            <p className="rounded-xl bg-[#FDECEF] px-3 py-2 text-[13px] text-[#B91C1C]">{notice}</p>
          </div>
        )}
        <div className="flex flex-shrink-0 gap-3 border-t border-[#EEF0F4] px-7 py-4">
          <BigButton kind="secondary" onClick={onClose} className="flex-1">Annuler</BigButton>
          <BigButton onClick={save} disabled={saving} className="flex-1">
            {saving ? 'Enregistrement…' : isEdit ? 'Enregistrer' : 'Créer l’annonce'}
          </BigButton>
        </div>
      </div>

      {pickerTarget && (
        <LocationPicker
          vehicleType={form.vehicle_type}
          initialLat={pickerTarget === 'pickup'
            ? (form.latitude ? parseFloat(form.latitude) : undefined)
            : (form.return_latitude ? parseFloat(form.return_latitude) : undefined)}
          initialLng={pickerTarget === 'pickup'
            ? (form.longitude ? parseFloat(form.longitude) : undefined)
            : (form.return_longitude ? parseFloat(form.return_longitude) : undefined)}
          onLocationSelect={(lat: number, lng: number, address: string) => {
            // L'adresse suit l'épingle, comme dans l'app : le libellé décrit le
            // point, et c'est lui seul que le mobile affiche. Une adresse
            // gardée telle quelle après avoir déplacé l'épingle mentirait ; le
            // champ reste modifiable à la main après coup.
            if (pickerTarget === 'pickup') {
              setForm((prev) => ({
                ...prev,
                latitude: lat.toString(),
                longitude: lng.toString(),
                pickup_address: address
              }));
            } else {
              setForm((prev) => ({
                ...prev,
                return_latitude: lat.toString(),
                return_longitude: lng.toString(),
                return_address: address
              }));
            }
            setPickerTarget(null);
          }}
          onClose={() => setPickerTarget(null)}
        />
      )}
    </div>
  );
};

export default VehicleEditor;
